import { addLocaleSeparators, getDecimalSeparator, getGroupSeparator } from './numberUtils'

export function removeGroupingSpaces(input: string): string {
  // Remove normal spaces and non-breaking/thin spaces often used as grouping separators.
  return input.replace(/[\s\u00A0\u202F]/g, '')
}

export function removeGroupingSeparator(input: string): string {
  // Remove the locale-specific grouping character from a formatted display value.
  // Only remove separators that look like thousands grouping (e.g. "1,234") so we don't corrupt decimal comma inputs (e.g. "1,5").
  const localeSeparator = getGroupSeparator()
  if (localeSeparator === ' ' || localeSeparator === '\u00A0' || localeSeparator === '\u202F') {
    return removeGroupingSpaces(input)
  }
  const escaped = localeSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const groupingRegex = new RegExp(`(\\d)${escaped}(?=\\d{3}(\\D|$))`, 'g')
  return input.replace(groupingRegex, '$1')
}

const LONE_COMMA_BEFORE_THREE_DIGITS = /^-?\d+,\d{3}$/

export function normalizeDecimal(input: string): string {
  const withoutLocaleSeparator = removeGroupingSeparator(input)
  const compact = removeGroupingSpaces(withoutLocaleSeparator).trim()
  if (getDecimalSeparator() === ',' && LONE_COMMA_BEFORE_THREE_DIGITS.test(compact)) {
    return compact.replace(',', '.')
  }
  // Remove commas that are used as thousands separators (e.g. "1,234,567" or "1,234.5").
  // Keep a single comma when it's acting as the decimal separator (e.g. "1,5").
  const withoutCommaGrouping = compact.replace(/(\d),(?=\d{3}(\D|$))/g, '$1')
  return withoutCommaGrouping.replace(',', '.')
}

export function serializeDecimalInputText(value: number): string {
  const text = String(value)
  return getDecimalSeparator() === ',' ? text.replace('.', ',') : text
}

export function formatBidInputForDisplay(rawValue: string): string {
  const trimmed = rawValue.trim()

  if (trimmed === '') {
    return ''
  }

  const compact = removeGroupingSpaces(trimmed)
  const match = compact.match(/^(-?\d+)([.,](\d+))?$/)

  if (!match) {
    return trimmed
  }

  const integerPart = match[1]
  const typedSeparator = match[2] ? match[2][0] : ''
  const decimalPart = match[3] ?? ''

  const groupedInteger = addLocaleSeparators(integerPart)

  if (!typedSeparator) {
    return groupedInteger
  }

  // Render the decimal with the locale separator so a typed "." or "," matches the
  // language (e.g. sv-SE/de-DE: "10000.33" -> "10 000,33"; en-GB: "1,000.33").
  const decimalSeparator = getDecimalSeparator()

  return `${groupedInteger}${decimalSeparator}${decimalPart}`
}

export function parseDecimal(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined) {
    return null
  }

  const normalized = typeof input === 'number' ? input.toString() : normalizeDecimal(input)
  if (normalized === '') {
    return null
  }

  // Require full decimal tokens. Examples rejected: "9.", "9,", ".9", "-.", "-".
  if (!/^-?\d+([.,]\d+)?$/.test(normalized)) {
    return null
  }

  const value = Number(normalized)
  if (!Number.isFinite(value)) {
    return null
  }

  return value === 0 ? 0 : value
}

export function parseNonNegativeDecimal(input: string | number | null | undefined): number | null {
  if (typeof input === 'string' && (input.trim().startsWith('-') || normalizeDecimal(input).startsWith('-'))) {
    return null
  }
  if (typeof input === 'number' && Object.is(input, -0)) {
    return null
  }

  const value = parseDecimal(input)
  if (value === null || value < 0) {
    return null
  }

  return value
}
